import { AnchorButton, Button } from "@svea/design-system-components-react";
import {
	ButtonGroup,
	formatOrgOrPersonNumber,
	Pill,
	Stack,
	Text,
	useText,
} from "@sveabank/cc-common";
import { format } from "date-fns";
import { useState } from "react";
import Paper from "../../components/paper";
import type { ClcApplication } from "../../types/clc-application";
import { DeleteClcModal } from "../new-company/components/delete-modal";

type Props = { application: ClcApplication };

const OngoingClcApplication = ({ application }: Props) => {
	const [t, texts] = useText();

	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const getClcStatus = () => {
		switch (application.clcStatus) {
			case "Ongoing":
				return (
					<>
						<Pill
							text={`${t(
								texts.dashboardPage.applications.statuses.ongoing,
							)} ${format(application.initiatedAt, "yyyy-MM-dd")}`}
							status="pending"
						/>
						<Text className="text-sm">{t(texts.newCompany.ongoing.description)}</Text>
					</>
				);
			case "InReview":
				return (
					<>
						<Pill text={t(texts.newCompany.inReview.status)} status="success" />
						<Text className="text-sm">{t(texts.newCompany.inReview.description)}</Text>
					</>
				);
			case "AwaitingPayment":
				return (
					<>
						<Pill text={t(texts.newCompany.awaitingPayment.status)} status="pending" />
						<Text className="text-sm">{t(texts.newCompany.awaitingPayment.description)}</Text>
					</>
				);
			case "PaymentCompleted":
				return (
					<>
						<Pill text={t(texts.newCompany.paymentCompleted.status)} status="success" />
						<Text className="text-sm">{t(texts.newCompany.paymentCompleted.description)}</Text>
					</>
				);
			case "AwaitingOrgNumber":
				return (
					<>
						<Pill text={t(texts.newCompany.awaitingOrgNumber.status)} status="pending" />
						<Text className="text-sm">{t(texts.newCompany.awaitingOrgNumber.description)}</Text>
					</>
				);
			case "OrgNumberSet":
				return (
					<>
						<Pill text={t(texts.newCompany.orgNumberSet.status)} status="success" />
						<Text className="text-sm">{t(texts.newCompany.orgNumberSet.description)}</Text>
					</>
				);
			default:
				break;
		}
	};

	return (
		<>
			<Paper padding="base">
				<Stack gap={2}>
					{getClcStatus()}

					<Stack gap={2}>
						<Stack gap={1}>
							<Text component="h4" className="text-text-primary text-xl" bold>
								{application.registeredCompanyName ||
									application.proposedCompanyName ||
									t(texts.dashboardPage.applications.newCompanyApplication.noCompanyName)}
							</Text>

							{application.connectedOrgNumber && (
								<Text>{formatOrgOrPersonNumber(application.connectedOrgNumber)}</Text>
							)}
						</Stack>

						<ButtonGroup align="start" row>
							{application.clcStatus === "Ongoing" ||
							application.clcStatus === "AwaitingPayment" ? (
								<>
									<AnchorButton
										href={`/new-company/${application.applicationId}`}
										iconEnd="chevronRight"
									>
										{application.clcStatus === "AwaitingPayment"
											? t(texts.newCompany.paymentRemainderModal.primaryButtonText)
											: t(texts.common.continue)}
									</AnchorButton>
									{application.clcStatus === "Ongoing" && (
										<Button
											onPress={() => setIsDeleteModalOpen(true)}
											variant="secondary"
											iconStart="trash"
										>
											{t(texts.common.deleteApplication)}
										</Button>
									)}
								</>
							) : (
								<Stack gap={2}>
									{application.connectedOrgNumber && (
										<Text className="text-sm">{t(texts.dashboardPage.newCompanyVerksamtText)}</Text>
									)}

									<AnchorButton
										href={`/new-company-summary/${application.applicationId}`}
										iconEnd="chevronRight"
									>
										{t(texts.dashboardPage.newCompanySummaryLink)}
									</AnchorButton>
								</Stack>
							)}
						</ButtonGroup>
					</Stack>
				</Stack>
			</Paper>
			<DeleteClcModal
				applicationId={application.applicationId}
				open={isDeleteModalOpen}
				closeModal={() => setIsDeleteModalOpen(false)}
			/>
		</>
	);
};

export default OngoingClcApplication;
